/**
 * Permission Queries
 *
 * React Query hooks for permission checking.
 * Uses permissionService for data fetching with automatic caching.
 *
 * Part of PE-898/PE-900: Dynamic permissions fetching
 */

import { useQuery } from '@tanstack/react-query';
import {
  getCurrentUser,
  getCurrentUserPermissions,
  getCurrentUserRoles,
  getCurrentUserRole,
  getCachedPermissions,
  getCachedCurrentUser,
  isSystemAdmin,
  invalidatePermissionCache,
} from '../permissionService';
import type { ApplicationType, CurrentUser, UserPermissions, Role } from '../permissionService';
import { useAuth } from '../../contexts/AuthContext';
import { useAbilityContext } from '../../contexts/AbilityContext';
import { shouldBypassRoleChecks } from '../../config/mvpConfig';
import { shouldUseGobodo } from '../../utils/gobodoUtils';
import { fetchCategoryAdminAccess } from '../gobodo/gobodoAuthorizationService';
import { computeCanAccessApp, mergeAccessibleAppsList } from './permissionAccessUtils';

// Re-export types
export type { ApplicationType, CurrentUser, UserPermissions, Role };

export { computeCanAccessApp, mergeAccessibleAppsList } from './permissionAccessUtils';

// Query key factory
export const permissionKeys = {
  all: ['permissions'] as const,
  currentUser: () => [...permissionKeys.all, 'currentUser'] as const,
  currentRoles: () => [...permissionKeys.all, 'currentRoles'] as const,
  currentRole: () => [...permissionKeys.all, 'currentRole'] as const,
  currentPermissions: () => [...permissionKeys.all, 'currentPermissions'] as const,
  canEdit: (context: ApplicationType) => [...permissionKeys.all, 'canEdit', context] as const,
  canAccess: (app: ApplicationType) => [...permissionKeys.all, 'canAccess', app] as const,
  categoryAdmin: () => [...permissionKeys.all, 'categoryAdmin'] as const,
};

/**
 * Gobodo CATEGORY_ADMIN via AbilitiesApi.checkAuthorization (cached).
 */
export function useCategoryAdminAccess() {
  const { isAuthenticated } = useAuth();
  const bypass = shouldBypassRoleChecks();
  const useGobodo = shouldUseGobodo();
  const enabled = isAuthenticated && !bypass && useGobodo;

  return useQuery({
    queryKey: permissionKeys.categoryAdmin(),
    queryFn: fetchCategoryAdminAccess,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled,
  });
}

/**
 * Hook to get current user info with role and permissions
 */
export function useCurrentUser() {
  return useQuery({
    queryKey: permissionKeys.currentUser(),
    queryFn: getCurrentUser,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    refetchOnWindowFocus: false,
    // Provide cached data immediately while fetching
    placeholderData: getCachedCurrentUser,
  });
}

/**
 * Hook to get current user's roles
 */
export function useCurrentUserRoles() {
  return useQuery({
    queryKey: permissionKeys.currentRoles(),
    queryFn: getCurrentUserRoles,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to get current user's role
 */
export function useCurrentUserRole() {
  return useQuery({
    queryKey: permissionKeys.currentRole(),
    queryFn: getCurrentUserRole,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to get current user's permissions
 */
export function useCurrentUserPermissions() {
  return useQuery({
    queryKey: permissionKeys.currentPermissions(),
    queryFn: getCurrentUserPermissions,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: getCachedPermissions,
  });
}

/**
 * Hook to check if user can edit in a specific context
 * Returns { canEdit, isLoading, error }
 */
export function useCanEdit(context: ApplicationType) {
  const { data: permissions, isLoading: permissionsLoading, error } = useCurrentUserPermissions();
  const { data: categoryAdmin, isLoading: categoryAdminLoading } = useCategoryAdminAccess();
  const { isAuthenticated } = useAuth();
  const categoryAdminEnabled = isAuthenticated && !shouldBypassRoleChecks() && shouldUseGobodo();

  let provisionalEdit = false;
  if (permissions) {
    switch (context) {
      case 'cybersweep':
        provisionalEdit = permissions.canEditCyberSweep;
        break;
      case 'credit_block':
        provisionalEdit = permissions.canEditCreditBlock;
        break;
      case 'credit_management':
        provisionalEdit = permissions.canEditCreditManagement;
        break;
      case 'regal_main':
        provisionalEdit = permissions.canEdit;
        break;
    }
  }

  const canEdit = Boolean(categoryAdmin) || provisionalEdit;
  const isLoading =
    permissionsLoading ||
    (categoryAdminEnabled && categoryAdminLoading && !provisionalEdit && !categoryAdmin);

  return { canEdit, isLoading, error };
}

/**
 * Hook to check if user can access an app
 * Returns { canAccess, isLoading, error }
 */
export function useCanAccessApp(app: ApplicationType) {
  const { data: permissions, isLoading: permissionsLoading, error } = useCurrentUserPermissions();
  const { ability, isLoading: abilityLoading } = useAbilityContext();
  const { data: categoryAdmin, isLoading: categoryAdminLoading } = useCategoryAdminAccess();
  const { isAuthenticated } = useAuth();
  const categoryAdminEnabled = isAuthenticated && !shouldBypassRoleChecks() && shouldUseGobodo();

  const provisionalAllow =
    (permissions?.accessibleApps.includes(app) ?? false) || ability.can('access', app);

  const canAccess = computeCanAccessApp(app, {
    accessibleApps: permissions?.accessibleApps,
    categoryAdmin,
    ability,
  });

  const isLoading =
    permissionsLoading ||
    abilityLoading ||
    (categoryAdminEnabled && categoryAdminLoading && !provisionalAllow && !categoryAdmin);

  return { canAccess, isLoading, error };
}

/**
 * Hook to check if user is system admin
 */
export function useIsSystemAdmin() {
  const { data: user, isLoading } = useCurrentUser();

  return {
    isSystemAdmin:
      user?.roles?.some((role) => role.isSystem || role.name === 'System Administrator') ||
      user?.role?.isSystem ||
      user?.role?.name === 'System Administrator' ||
      isSystemAdmin(),
    isLoading,
  };
}

/**
 * Hook to get accessible apps for current user
 */
export function useAccessibleApps() {
  const { data: permissions, isLoading, error } = useCurrentUserPermissions();
  const { data: categoryAdmin } = useCategoryAdminAccess();

  return {
    apps: mergeAccessibleAppsList(permissions?.accessibleApps, categoryAdmin),
    isLoading,
    error,
  };
}

// Re-export cache invalidation for logout handlers
export { invalidatePermissionCache };